// @flow
/*

  This component is a selector-based time input. We use it in situations where
  a native <input type="time" /> is not supported. For example, as of this writing
  desktop Safari does not support the time input.

  It accepts props.value of the form 'HH:mm' (24-hour clock) or `null`

  Internally, it uses '--' to represent `null`. If a user selects `--` on any input, the `onChange`
  handler is called with `null`. Assuming this sets props.value to `null`, all three selectors
  should change to `--`

  props.locale uses a 12-hour clock with AM / PM selectors if the user provides `locale === 'en'`,
  but otherwise uses a 24-hour clock with no AM / PM selector.

  The `onChange` handler is called with a new 24-hour value of the form 'HH:mm' or `null` always.

*/

import * as React from 'react';
import invariant from 'invariant';
import styled from 'styled-components';
import SelectInput from 'components/SelectInput';
import { range } from 'utils/functional';

const InputContainer = styled.div`
  display: inline-flex;
  width: 100%;
  position: relative;
  align-items: center;
`;

const timeNumberToDisplay = (v: number) => (v < 10 ? `0${v}` : String(v));

const parseHourMinute = (
  displayValue: string
): { hour24: number, minute: number } => {
  const [hour, minute] = displayValue.split(':');
  return {
    hour24: parseInt(hour, 10),
    minute: parseInt(minute, 10)
  };
};

const hourNumTo24 = (
  hourOptionValue: number,
  isAmpm: boolean,
  ampmValue: 'AM' | 'PM'
) => {
  if (!isAmpm) {
    return timeNumberToDisplay(hourOptionValue);
  }
  if (ampmValue === 'PM') {
    return timeNumberToDisplay(
      hourOptionValue < 12 ? hourOptionValue + 12 : hourOptionValue
    );
  }
  return timeNumberToDisplay(
    hourOptionValue < 12 ? hourOptionValue : hourOptionValue - 12
  );
};

const isDefined = (x: any): boolean %checks =>
  x !== null && typeof x !== 'undefined';

const NULL_DISPLAY = '--';
const NULL_VALUE = '--';

type Props = {
  locale: string,
  onChange: (?string) => void,
  value: ?string,
  datetimeProps: Object
};

type State = {
  ampm: 'AM' | 'PM'
};

class TimeInput extends React.PureComponent<Props, State> {
  state = { ampm: 'AM' };

  // Whether the component will use a 12-hour clock
  isAmpm() {
    return this.props.locale === 'en';
  }

  componentDidMount() {
    if (
      this.isAmpm() &&
      this.props.value &&
      parseHourMinute(this.props.value).hour24 > 11
    ) {
      this.setState({ ampm: 'PM' });
    }
  }

  // Called when the user changes selectors with `v`, the new (display)
  // value from SelectorInput
  onChange = (fieldName: 'HOUR' | 'MINUTE' | 'AMPM') => (v: string) => {
    if (v === NULL_DISPLAY) {
      this.props.onChange(null);
      return;
    }
    const { hour24: prevHour24, minute: prevMinute } = this.props.value
      ? parseHourMinute(this.props.value)
      : { hour24: null, minute: null };
    if (fieldName === 'MINUTE') {
      const newValue = parseInt(v, 10);
      const newHourValue = isDefined(prevHour24)
        ? timeNumberToDisplay(prevHour24)
        : '01';
      const newMinuteValue = timeNumberToDisplay(newValue);
      this.props.onChange(`${newHourValue}:${newMinuteValue}`);
    } else if (fieldName === 'HOUR') {
      const newValue = parseInt(v, 10);
      const newMinuteValue = isDefined(prevMinute)
        ? timeNumberToDisplay(prevMinute)
        : '00';
      const newHourValue = hourNumTo24(
        newValue,
        this.isAmpm(),
        this.state.ampm
      );
      this.props.onChange(`${newHourValue}:${newMinuteValue}`);
    } else if (fieldName === 'AMPM') {
      const newValue = v;
      invariant(
        newValue === 'AM' || newValue === 'PM',
        'AM or PM are the only acceptable values'
      );
      this.setState({ ampm: newValue });
      const newHourValue = isDefined(prevHour24)
        ? hourNumTo24(prevHour24, this.isAmpm(), newValue)
        : '01';
      const newMinuteValue = isDefined(prevMinute)
        ? timeNumberToDisplay(prevMinute)
        : '00';
      this.props.onChange(`${newHourValue}:${newMinuteValue}`);
    }
  };

  render() {
    const ampm = this.isAmpm();
    let hourValue = NULL_VALUE;
    let ampmValue = NULL_VALUE;
    let minuteValue = NULL_VALUE;
    if (this.props.value) {
      const value = this.props.value;
      const { hour24, minute } = parseHourMinute(value);
      hourValue =
        ampm && hour24 > 12 ? hour24 - 12 : ampm && hour24 === 0 ? 12 : hour24;
      // TODO: will putting NULL_VALUE here cause 24 hour clock problems?
      ampmValue = ampm ? (hour24 >= 12 ? 'PM' : 'AM') : NULL_VALUE;
      minuteValue = minute;
    }
    const nullOption = { label: NULL_DISPLAY, value: NULL_VALUE };

    const hourOptions = (ampm ? range(1, 13) : range(0, 24)).map(v => ({
      label: timeNumberToDisplay(v),
      value: String(v)
    }));

    const minuteOptions = range(0, 60).map(v => ({
      label: timeNumberToDisplay(v),
      value: String(v)
    }));

    const ampmOptions = [
      { label: 'AM', value: 'AM' },
      { label: 'PM', value: 'PM' }
    ];

    const { name, ...otherProps } = this.props.datetimeProps;

    return (
      <InputContainer {...otherProps}>
        <SelectInput
          value={String(hourValue)}
          name={`${name}-hour`}
          height="100%"
          width="100%"
          options={hourOptions.concat([nullOption])}
          onChange={this.onChange('HOUR')}
        />
        <SelectInput
          value={String(minuteValue)}
          name={`${name}-minute`}
          height="100%"
          width="100%"
          options={minuteOptions.concat([nullOption])}
          onChange={this.onChange('MINUTE')}
        />
        {ampm && (
          <SelectInput
            value={ampmValue}
            name={`${name}-ampm`}
            height="100%"
            width="100%"
            options={ampmOptions.concat([nullOption])}
            onChange={this.onChange('AMPM')}
          />
        )}
      </InputContainer>
    );
  }
}

export default TimeInput;
